import { isGiftWrapped } from "../helpers/gift-wrap";

// Joiners, variation selectors and skin-tone modifiers go too: left behind
// once the pictograph is stripped, they're invisible but still use up the limit.
const EMOJI =
  /[\p{Extended_Pictographic}\p{Emoji_Modifier}\p{Regional_Indicator}‍️]/gu;

/**
 * The cart's gift message, saved as the `gift_message` cart attribute. Morph
 * only updates this element's `hidden` attribute (see snippet.cart-content), so
 * the panel and any unsaved draft survive a cart re-render; the saved message is
 * synced from the cart on `updateCart` instead.
 */
class GiftNote extends HTMLElement {
  onUpdateCart = (e) => {
    const cart = e.detail.cart;
    if (!Array.isArray(cart?.items)) return;

    const hasGiftWrap = cart.items.some(isGiftWrapped);

    // The first gift wrap in opens the message, as on the legacy theme. Focus
    // stays on the gift-wrap checkbox the visitor just used.
    if (hasGiftWrap && !this.hasGiftWrap) this.open({ focus: false });
    this.hasGiftWrap = hasGiftWrap;

    // Cleared with the last gift wrap — see cart-content.js
    if (this.savedMessage && !cart.attributes?.gift_message) {
      this.savedMessage = "";
      this.input.value = "";
      this._setSaved(false);
      this._renderCount();
    }
  };

  onInput = () => {
    const { value, selectionStart } = this.input;
    const stripped = value.replace(EMOJI, "");

    if (stripped === value) {
      this._renderCount();
      return;
    }

    const caret = selectionStart - (value.length - stripped.length);
    this.input.value = stripped;
    this.input.setSelectionRange(caret, caret);
    this.count.textContent = this.dataset.emojiMessage;
  };

  onOpenClick = () => this.open();
  onCloseClick = () => this.close();
  onSaveClick = () => this.save();
  onEditClick = () => {
    this._setSaved(false);
    this.input.focus();
  };

  connectedCallback() {
    this.openButton = this.querySelector("[data-gift-note-open]");
    this.panel = this.querySelector("[data-gift-note-panel]");
    this.input = this.querySelector("[data-gift-note-input]");
    this.count = this.querySelector("[data-gift-note-count]");
    this.saveButton = this.querySelector("[data-gift-note-save]");
    this.editButton = this.querySelector("[data-gift-note-edit]");
    this.closeButton = this.querySelector("[data-gift-note-close]");

    this.savedMessage = this.input.value;
    this.hasGiftWrap = !this.hidden;

    this.openButton.addEventListener("click", this.onOpenClick);
    this.closeButton.addEventListener("click", this.onCloseClick);
    this.saveButton.addEventListener("click", this.onSaveClick);
    this.editButton.addEventListener("click", this.onEditClick);
    this.input.addEventListener("input", this.onInput);
    document.addEventListener("updateCart", this.onUpdateCart);
  }

  disconnectedCallback() {
    this.openButton.removeEventListener("click", this.onOpenClick);
    this.closeButton.removeEventListener("click", this.onCloseClick);
    this.saveButton.removeEventListener("click", this.onSaveClick);
    this.editButton.removeEventListener("click", this.onEditClick);
    this.input.removeEventListener("input", this.onInput);
    document.removeEventListener("updateCart", this.onUpdateCart);
  }

  open({ focus = true } = {}) {
    this.panel.hidden = false;
    this.openButton.hidden = true;
    if (focus) this.input.focus();
  }

  close() {
    this.panel.hidden = true;
    this.openButton.hidden = false;
    this.openButton.focus();
  }

  save() {
    const message = this.input.value.trim();

    this.saveButton.disabled = true;
    this.input.classList.remove("error");

    fetch(`${window.routes.cart_update_url}.js`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ attributes: { gift_message: message } }),
    })
      .then((response) => {
        if (!response.ok) throw new Error(response.statusText);

        this.savedMessage = message;
        this.input.value = message;
        this._renderCount();
        this._setSaved(Boolean(message));
      })
      .catch((error) => {
        this.input.classList.add("error");
        console.error("Gift message failed to save:", error);
      })
      .finally(() => {
        this.saveButton.disabled = false;
      });
  }

  /** A saved message is read-only until the visitor chooses to edit it. */
  _setSaved(saved) {
    this.input.readOnly = saved;
    this.saveButton.hidden = saved;
    this.editButton.hidden = !saved;

    if (saved) this.editButton.focus();
  }

  _renderCount() {
    this.count.textContent = this.dataset.countMessage.replace(
      "[count]",
      this.input.value.length,
    );
  }
}

if (!customElements.get("gift-note")) {
  customElements.define("gift-note", GiftNote);
}
